import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type JSX,
  type ReactNode,
} from "react";
import { sync, SyncService } from "./SyncService.js";
import { db } from "./db.js";
import { useAuth } from "../features/auth/AuthProvider.js";

interface SyncContextValue {
  service: SyncService;
}

const SyncContext = createContext<SyncContextValue | null>(null);

export function useSync(): SyncContextValue {
  const v = useContext(SyncContext);
  if (!v) throw new Error("useSync must be used inside SyncProvider");
  return v;
}

const DELTA_INTERVAL_MS = 5 * 60 * 1000;
const ACTIVITY_WINDOW_MS = 60 * 1000;

export function SyncProvider({ children }: { children: ReactNode }): JSX.Element {
  const auth = useAuth();
  const bootstrappedRef = useRef(false);
  const lastActivityRef = useRef<number>(Date.now());

  // First sync per authenticated mount. bootstrap() is a ONE-TIME full sync (spec
  // §6.2) — run it only when no sync_state watermark exists yet. On every later app
  // open the watermark is present, so we run delta() instead. Re-running bootstrap on
  // every load would resurrect partner-deleted rows: bootstrap filters tombstones
  // and jumps the watermark forward so the tombstone is never re-fetched by a delta.
  useEffect(() => {
    if (auth.status !== "authenticated") {
      bootstrappedRef.current = false;
      return;
    }
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    void (async () => {
      try {
        const hasWatermark = (await db.sync_state.get("recipes")) != null;
        await (hasWatermark ? sync.delta() : sync.bootstrap());
      } catch (err: unknown) {
        // User-visible retry surfacing deferred per Plan 04 scope; at least log
        // instead of leaving an unhandled rejection.
        console.error("sync initial load failed", err);
      }
    })();
  }, [auth.status]);

  // Activity tracking — last event timestamp.
  useEffect(() => {
    function bump(): void {
      lastActivityRef.current = Date.now();
    }
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    for (const ev of events) document.addEventListener(ev, bump, { passive: true });
    return () => {
      for (const ev of events) document.removeEventListener(ev, bump);
    };
  }, []);

  // Delta on focus + 5-min interval (gated by visibility + recent activity).
  useEffect(() => {
    if (auth.status !== "authenticated") return;

    function fireIfEligible(force: boolean): void {
      if (document.visibilityState !== "visible") return;
      const recent = Date.now() - lastActivityRef.current < ACTIVITY_WINDOW_MS;
      if (!force && !recent) return;
      sync.delta().catch((err: unknown) => {
        console.error("sync.delta failed", err);
      });
    }

    function onVisibility(): void {
      if (document.visibilityState === "visible") fireIfEligible(true);
    }

    function onFocus(): void {
      fireIfEligible(true);
    }

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);

    const interval = window.setInterval(() => {
      fireIfEligible(false);
    }, DELTA_INTERVAL_MS);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
      window.clearInterval(interval);
    };
  }, [auth.status]);

  const value = useMemo<SyncContextValue>(() => ({ service: sync }), []);
  return <SyncContext.Provider value={value}>{children}</SyncContext.Provider>;
}
